import { useRef, useState, useLayoutEffect, useEffect } from 'react';

/**
 * 네이티브 HTML5 드래그앤드롭 순서 변경 + 드롭 위치 표시 + FLIP 슬라이드 애니메이션.
 *
 * 사용:
 *   const { containerRef, dragProps, dropProps, cardClass } = useDragReorder(reorder);
 *   <div className="svc-cards" ref={containerRef}>
 *     {items.map((s,i)=>(
 *       <div className={`s-card ${cardClass(i)}`} data-flip={s.slug} {...dropProps(i)}>
 *         <span className="drag-handle" {...dragProps(i)}>⠿</span>
 *   - data-flip: 카드별 안정 키(슬러그/intNum 등). FLIP이 위치 변화를 측정·애니메이션.
 *   - onReorder(from, to): from→to 이동.
 */
export function useDragReorder(onReorder) {
  const containerRef = useRef(null);
  const positions = useRef(new Map());
  const from = useRef(null);
  const justReordered = useRef(false); // onDrop 직후에만 FLIP 실행
  const [fromIdx, setFromIdx] = useState(null);
  const [over, setOver] = useState(null);

  // ── Edge-scroll: 드래그 중 뷰포트 상하 80px 진입 시 자동 스크롤 ──
  const dragY = useRef(null);   // 현재 마우스 Y (null = 드래그 중 아님)
  const rafScroll = useRef(null);

  const startScroll = () => {
    const EDGE = 80;      // 엣지 감지 범위(px)
    const MAX_SPD = 16;   // 최대 스크롤 속도(px/frame)
    const tick = () => {
      if (dragY.current != null) {
        const y = dragY.current;
        const vh = window.innerHeight;
        let spd = 0;
        if (y < EDGE)        spd = -MAX_SPD * (1 - y / EDGE);
        else if (y > vh - EDGE) spd =  MAX_SPD * (1 - (vh - y) / EDGE);
        if (spd !== 0) window.scrollBy(0, spd);
      }
      rafScroll.current = requestAnimationFrame(tick);
    };
    rafScroll.current = requestAnimationFrame(tick);
  };

  const stopScroll = () => {
    if (rafScroll.current) { cancelAnimationFrame(rafScroll.current); rafScroll.current = null; }
    dragY.current = null;
  };

  // dragover 는 드래그 중 마우스 위치를 알 수 있는 유일한 이벤트
  useEffect(() => {
    const update = (e) => { if (dragY.current != null) dragY.current = e.clientY; };
    window.addEventListener('dragover', update);
    return () => window.removeEventListener('dragover', update);
  }, []);

  // FLIP: 렌더 후 카드 위치가 바뀌었으면 이전 위치에서 현재 위치로 부드럽게 이동
  useLayoutEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const cards = c.querySelectorAll(':scope > [data-flip]');

    // 진행 중인 애니메이션을 먼저 취소해 자연 위치를 측정한다.
    // (애니메이션 도중 추가 렌더가 오면 getBoundingClientRect가 중간 transform 위치를
    //  반환해 잘못된 dx/dy가 계산되는 문제를 방지)
    cards.forEach((el) => {
      el.style.transition = 'none';
      el.style.transform = '';
    });

    const next = new Map();
    const isDragging = from.current != null;
    // 드래그 드롭으로 실제 재정렬이 일어난 직후에만 FLIP 애니메이션 실행
    const shouldAnimate = !isDragging && justReordered.current;
    if (shouldAnimate) justReordered.current = false;
    cards.forEach((el) => {
      const k = el.getAttribute('data-flip');
      const r = el.getBoundingClientRect();
      const absX = r.left + window.scrollX;
      const absY = r.top + window.scrollY;
      next.set(k, { x: absX, y: absY });
      if (shouldAnimate) {
        const p = positions.current.get(k);
        if (p) {
          const dx = p.x - absX;
          const dy = p.y - absY;
          if (dx || dy) {
            el.style.transform = `translate(${dx}px, ${dy}px)`;
            requestAnimationFrame(() => {
              el.style.transition = 'transform 220ms cubic-bezier(0.2, 0, 0, 1)';
              el.style.transform = '';
            });
          }
        }
      }
    });
    positions.current = next;
  });

  // 카드 어디를 잡아도 드래그되게 하되, 텍스트/버튼/이미지 등 대화형 요소를 누르면
  // 편집/클릭이 우선되도록 그 위에서 mousedown 시에는 draggable을 끈다.
  const INTERACTIVE = '.editable, input, textarea, button, a, select, .img-modal, [contenteditable="true"]';
  const dragProps = (i) => ({
    onMouseDown: (e) => {
      const interactive = e.target && e.target.closest && e.target.closest(INTERACTIVE);
      e.currentTarget.draggable = !interactive;
    },
    onDragStart: (e) => {
      dragY.current = e.clientY;
      from.current = i;
      setFromIdx(i);
      startScroll();
      if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(i)); } catch (_) {} }
    },
    onDragEnd: (e) => { stopScroll(); from.current = null; setFromIdx(null); setOver(null); e.currentTarget.draggable = false; },
  });

  const dropProps = (i) => ({
    onDragOver: (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; if (over !== i) setOver(i); },
    onDragLeave: (e) => { if (over === i && !e.currentTarget.contains(e.relatedTarget)) setOver(null); },
    onDrop: (e) => {
      e.preventDefault();
      const f = from.current;
      from.current = null; setOver(null); setFromIdx(null);
      if (f == null || f === i) return;
      justReordered.current = true; // 재정렬 발생 → 다음 FLIP 허용
      onReorder(f, i);
    },
  });

  const cardClass = (i) => `${i === fromIdx ? 'dnd-dragging' : ''} ${over === i && i !== fromIdx ? 'dnd-over' : ''}`.trim();

  return { containerRef, dragProps, dropProps, cardClass };
}
